import { API_HOST, STORAGE_KEY } from "@/lib/config"

const TRPC_BASE = `${API_HOST}/trpc`

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
  method?: "GET" | "POST",
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const url = `${TRPC_BASE}/${procedure}`
  const fetchOptions: RequestInit = { headers }

  if (method === "GET" || (method === undefined && input === undefined)) {
    fetchOptions.method = "GET"
  } else {
    fetchOptions.method = "POST"
    if (input !== undefined) {
      fetchOptions.body = JSON.stringify(input)
    }
  }

  const response = await fetch(url, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

// ─── Types ──────────────────────────────────────────────────────

export interface DiagnosisResult {
  id: string
  diagnosis_type: string
  overall_level: "weak" | "medium" | "strong"
  result: {
    overallLevel: string
    subjectBreakdown: Array<{
      subject: string
      score: number
      trend: "improving" | "stable" | "declining"
      weakTopics: string[]
      strongTopics: string[]
    }>
    weakPoints: Array<{
      kpCode: string
      name: string
      severity: "high" | "medium" | "low"
      evidence: string
    }>
  }
  created_at: string
}

export interface CourseListItem {
  id: string
  title: string
  course_type: string
  subject: string
  status: string
  total_lessons: number
  completed_lessons: number
  created_at: string
}

export interface CourseDetail extends CourseListItem {
  lessons: LessonItem[]
}

export interface LessonItem {
  id: string
  index_in_course: number
  title: string
  objectives: string[]
  duration: number
  status: "not_started" | "in_progress" | "completed"
  score?: number
  slide_html?: string
  practice_question_ids?: string[]
}

export interface DailyTaskItem {
  type: "lesson" | "practice" | "retry" | "review"
  title: string
  refId: string
  duration: number
  status: "pending" | "done" | "skipped"
}

export interface DailyTasksResponse {
  date: string
  tasks: DailyTaskItem[]
}

export interface ChatResponse {
  threadId: string
  response: string
  diagnosis: any
  mistakeAnalysis: any
  coursePlan: any
  learningPlan: any
}

// ─── API Functions ──────────────────────────────────────────────

export async function triggerDiagnosis(input: {
  source: "upload" | "auto" | "manual"
  fileIds?: string[]
  examSessionIds?: string[]
  message?: string
}): Promise<{ diagnosisId: string; result: any }> {
  return tRPCRequest("aiTeam.diagnose", input)
}

export async function getDiagnosis(): Promise<DiagnosisResult | null> {
  return tRPCRequest("aiTeam.getDiagnosis", undefined, "GET")
}

export async function getDiagnosisHistory(limit = 10): Promise<DiagnosisResult[]> {
  return tRPCRequest("aiTeam.getDiagnosisHistory", { limit }, "GET")
}

export async function getQuickSummary(sessionId: string): Promise<{ summary: string }> {
  return tRPCRequest("aiTeam.getQuickSummary", { sessionId }, "GET")
}

export async function getMistakeAnalysis(): Promise<any> {
  return tRPCRequest("aiTeam.getMistakeAnalysis", undefined, "GET")
}

export async function generateCourse(input: {
  topic: string
  courseType: "preview" | "new" | "review" | "exam_prep" | "targeted"
  subject?: string
  kpCode?: string
  diagnosisId?: string
}): Promise<{ courseId: string | null; course: any }> {
  return tRPCRequest("aiTeam.generateCourse", input)
}

export async function listCourses(input?: {
  status?: string
  courseType?: string
}): Promise<CourseListItem[]> {
  return tRPCRequest("aiTeam.listCourses", input, "GET")
}

export async function getCourse(courseId: string): Promise<CourseDetail | null> {
  return tRPCRequest("aiTeam.getCourse", { courseId }, "GET")
}

export async function getLessonContent(lessonId: string): Promise<any> {
  return tRPCRequest("aiTeam.getLessonContent", { lessonId }, "GET")
}

export async function updateLessonProgress(
  lessonId: string,
  status: 'in_progress' | 'completed',
): Promise<void> {
  return tRPCRequest("aiTeam.updateLessonProgress", { lessonId, status })
}

export async function generateDailyTasks(date?: string): Promise<DailyTasksResponse> {
  return tRPCRequest("aiTeam.generateDailyTasks", { date })
}

export async function getDailyTasks(date?: string): Promise<DailyTasksResponse> {
  return tRPCRequest("aiTeam.getDailyTasks", { date }, "GET")
}

export async function updateTaskStatus(input: {
  date: string
  taskIndex: number
  status: "pending" | "done" | "skipped"
}): Promise<void> {
  return tRPCRequest("aiTeam.updateTaskStatus", input)
}

export async function chatWithTeam(input: {
  message: string
  threadId?: string
}): Promise<ChatResponse> {
  return tRPCRequest("aiTeam.chat", input)
}

export async function onExamSubmit(sessionId: string): Promise<{
  mistakeAnalysis: any
  learningPlan: any
}> {
  return tRPCRequest("aiTeam.onExamSubmit", { sessionId })
}
